import CartPopover from '@components/CartPopover';
import {
  AppBar,
  Badge,
  Button,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@material-ui/core';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import CloseIcon from '@material-ui/icons/Close';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import LanguageIcon from '@material-ui/icons/Language';
import MenuIcon from '@material-ui/icons/Menu';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import useCartStore from '@stores/cartStore';
import useLanguageStore from '@stores/languageStore';
import React, { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { cities } from '../../data/citiesData';
import { stoneTypes } from '../../data/stoneTypesData';

const ACCENT_COLOR = '#063447';
const ACCENT_COLOR_DARK = '#042a36';

const Navigation = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguageStore();
  const itemCount = useCartStore((state) => state.getItemCount());
  const [languageAnchorEl, setLanguageAnchorEl] = useState(null);
  const [cartAnchorEl, setCartAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Resources Menu State
  const [resourcesAnchorEl, setResourcesAnchorEl] = useState(null);
  const [serviceAreasAnchorEl, setServiceAreasAnchorEl] = useState(null);
  const [stoneTypesAnchorEl, setStoneTypesAnchorEl] = useState(null);
  const timeoutRef = useRef(null);

  // Mobile Expand State
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);
  const [mobileServiceAreasOpen, setMobileServiceAreasOpen] = useState(false);
  const [mobileStoneTypesOpen, setMobileStoneTypesOpen] = useState(false);

  const drawerId = useId();

  const handleLanguageMenuOpen = (event) => {
    setLanguageAnchorEl(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageAnchorEl(null);
  };

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    handleLanguageMenuClose();
  };

  const handleCartClick = (event) => {
    setCartAnchorEl(event.currentTarget);
  };

  const handleCartClose = () => {
    setCartAnchorEl(null);
  };

  const handleMobileMenuOpen = () => {
    setMobileMenuOpen(true);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
    setMobileResourcesOpen(false);
    setMobileServiceAreasOpen(false);
    setMobileStoneTypesOpen(false);
  };

  // Desktop Resources Menu Handlers
  const handleCloseResourceMenuRaw = () => {
    setResourcesAnchorEl(null);
    setServiceAreasAnchorEl(null);
    setStoneTypesAnchorEl(null);
  };

  const scheduleCloseResourceMenu = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(handleCloseResourceMenuRaw, 100);
  };

  const cancelCloseResourceMenu = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const handleResourcesMenuOpen = (event) => {
    cancelCloseResourceMenu();
    setResourcesAnchorEl(event.currentTarget);
  };

  const handleServiceAreasOpen = (event) => {
    cancelCloseResourceMenu();
    setServiceAreasAnchorEl(event.currentTarget);
    setStoneTypesAnchorEl(null);
  };

  const handleStoneTypesOpen = (event) => {
    cancelCloseResourceMenu();
    setStoneTypesAnchorEl(event.currentTarget);
    setServiceAreasAnchorEl(null);
  };

  const isRtl = language === 'he';

  const navItems = [
    { path: '/', label: t('navigation.home') },
    { path: '/about', label: t('navigation.about') },
    { path: '/gallery', label: t('navigation.gallery') },
    { path: '/projects', label: t('navigation.projects') },
    { path: '/services', label: t('navigation.services') },
    { path: '/contact', label: t('navigation.contact') },
  ];

  const renderIconsSection = () => (
    <div className="flex gap-1 items-center">
      <IconButton
        color="inherit"
        onClick={handleCartClick}
        style={{ backgroundColor: 'transparent' }}
        aria-label={t('navigation.cart', { count: itemCount })}
        aria-expanded={Boolean(cartAnchorEl)}
      >
        <Badge badgeContent={itemCount} color="error" aria-hidden="true">
          <ShoppingCartIcon />
        </Badge>
      </IconButton>
      <CartPopover anchorEl={cartAnchorEl} open={Boolean(cartAnchorEl)} onClose={handleCartClose} />
      <IconButton
        color="inherit"
        onClick={handleLanguageMenuOpen}
        style={{ backgroundColor: 'transparent' }}
        aria-label={t('navigation.changeLanguage')}
        aria-expanded={Boolean(languageAnchorEl)}
        aria-haspopup="true"
      >
        <LanguageIcon />
      </IconButton>
      <Menu
        anchorEl={languageAnchorEl}
        open={Boolean(languageAnchorEl)}
        onClose={handleLanguageMenuClose}
        role="menu"
        aria-label={t('navigation.languageMenu')}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isRtl ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: isRtl ? 'left' : 'right',
        }}
      >
        <MenuItem selected={language === 'he'} onClick={() => handleLanguageChange('he')}>
          עברית
        </MenuItem>
        <MenuItem selected={language === 'en'} onClick={() => handleLanguageChange('en')}>
          English
        </MenuItem>
      </Menu>
    </div>
  );

  const renderLogoSection = () => {
    const textAlign = isRtl ? 'right' : 'left';
    const alignItems = isRtl ? 'items-end' : 'items-start';
    const textAlignClass = isRtl ? 'text-right' : 'text-left';
    const justifyContent = isRtl ? 'flex-end' : 'flex-start';

    return (
      <div className="flex items-center gap-2">
        <IconButton
          color="inherit"
          onClick={handleMobileMenuOpen}
          className="flex md:!hidden"
          style={{ backgroundColor: 'transparent' }}
          aria-label={t('navigation.menu')}
          aria-expanded={mobileMenuOpen}
          aria-controls={drawerId}
        >
          <MenuIcon />
        </IconButton>

        <Button
          component={Link}
          to="/"
          color="inherit"
          className={`normal-case px-2 sm:px-4 py-2 transition-colors ${textAlignClass}`}
          style={{
            justifyContent,
            textAlign,
          }}
        >
          <div
            className={`flex flex-col ${alignItems} ${textAlignClass}`}
            style={{ textAlign, width: '100%' }}
          >
            <Typography
              variant="h6"
              className={`text-white leading-none !text-sm sm:!text-base ${textAlignClass}`}
              style={{ textAlign, width: '100%', fontWeight: 800 }}
              aria-label={t('navigation.logoAlt')}
            >
              {t('common.appName')}
            </Typography>
          </div>
        </Button>
      </div>
    );
  };

  return (
    <React.Fragment>
      <AppBar
        position="fixed"
        className="shadow-lg"
        style={{
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          backgroundColor: ACCENT_COLOR,
        }}
      >
        <Toolbar className="relative flex items-center justify-between px-4 sm:px-8 lg:px-24">
          <div className="flex-shrink-0">{renderLogoSection()}</div>

          {/* Desktop Nav */}
          <div className="hidden md:flex gap-1 items-center absolute left-1/2 transform -translate-x-1/2 w-max">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;

              return (
                <Button
                  key={item.path}
                  component={Link}
                  to={item.path}
                  color="inherit"
                  className="normal-case transition-colors text-white nav-button"
                  style={{
                    fontWeight: 700,
                    textDecoration: 'none',
                    backgroundColor: 'transparent',
                  }}
                >
                  <span
                    style={{
                      position: 'relative',
                      display: 'inline-block',
                      padding: '0 2px',
                    }}
                  >
                    {item.label}
                    {isActive && (
                      <span
                        aria-hidden="true"
                        style={{
                          position: 'absolute',
                          left: '-1px',
                          right: '-1px',
                          bottom: '-2px',
                          height: '2px',
                          backgroundColor: 'white',
                          pointerEvents: 'none',
                        }}
                      />
                    )}
                  </span>
                </Button>
              );
            })}

            {/* Resources Dropdown for Desktop */}
            <div
              role="button"
              tabIndex={0}
              onMouseLeave={scheduleCloseResourceMenu}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  scheduleCloseResourceMenu();
                }
              }}
            >
              <Button
                color="inherit"
                className="normal-case transition-colors text-white nav-button"
                style={{
                  fontWeight: 700,
                  textDecoration: 'none',
                  backgroundColor: 'transparent',
                }}
                aria-owns={resourcesAnchorEl ? 'resources-menu' : undefined}
                aria-haspopup="true"
                onMouseOver={handleResourcesMenuOpen}
              >
                {t('navigation.resources')}
              </Button>
              <Menu
                id="resources-menu"
                anchorEl={resourcesAnchorEl}
                open={Boolean(resourcesAnchorEl)}
                onClose={scheduleCloseResourceMenu}
                MenuListProps={{
                  onMouseEnter: cancelCloseResourceMenu,
                  onMouseLeave: scheduleCloseResourceMenu,
                }}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
              >
                {/* Service Areas Submenu Trigger */}
                <MenuItem
                  onClick={handleServiceAreasOpen}
                  onMouseEnter={handleServiceAreasOpen}
                  className="flex justify-between gap-4"
                >
                  {t('navigation.serviceAreas')}
                  {isRtl ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
                </MenuItem>

                {/* Stone Types Submenu Trigger */}
                <MenuItem
                  onClick={handleStoneTypesOpen}
                  onMouseEnter={handleStoneTypesOpen}
                  className="flex justify-between gap-4"
                >
                  {t('navigation.stoneTypes')}
                  {isRtl ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
                </MenuItem>

                {/* National Insurance Link - Direct Link */}
                <MenuItem
                  component={Link}
                  to="/national-insurance"
                  onClick={handleCloseResourceMenuRaw}
                  className="flex justify-between gap-4"
                >
                  {t('navigation.nationalInsurance')}
                </MenuItem>
              </Menu>

              {/* Service Areas Submenu */}
              <Menu
                id="service-areas-menu"
                anchorEl={serviceAreasAnchorEl}
                open={Boolean(serviceAreasAnchorEl)}
                onClose={scheduleCloseResourceMenu}
                MenuListProps={{
                  onMouseEnter: cancelCloseResourceMenu,
                  onMouseLeave: scheduleCloseResourceMenu,
                }}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: 'top', horizontal: isRtl ? 'left' : 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: isRtl ? 'right' : 'left' }}
                style={{ pointerEvents: 'none' }}
                PaperProps={{ style: { pointerEvents: 'auto' } }}
              >
                {cities.map((city) => (
                  <MenuItem
                    key={city.id}
                    component={Link}
                    to={`/cities/${city.id}`}
                    onClick={handleCloseResourceMenuRaw}
                  >
                    {city.name[language]}
                  </MenuItem>
                ))}
              </Menu>

              {/* Stone Types Submenu */}
              <Menu
                id="stone-types-menu"
                anchorEl={stoneTypesAnchorEl}
                open={Boolean(stoneTypesAnchorEl)}
                onClose={scheduleCloseResourceMenu}
                MenuListProps={{
                  onMouseEnter: cancelCloseResourceMenu,
                  onMouseLeave: scheduleCloseResourceMenu,
                }}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: 'top', horizontal: isRtl ? 'left' : 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: isRtl ? 'right' : 'left' }}
                style={{ pointerEvents: 'none' }}
                PaperProps={{ style: { pointerEvents: 'auto' } }}
              >
                {stoneTypes.map((stone) => (
                  <MenuItem
                    key={stone.id}
                    component={Link}
                    to={`/stones/${stone.slug}`}
                    onClick={handleCloseResourceMenuRaw}
                  >
                    {stone.name[language]}
                  </MenuItem>
                ))}
              </Menu>
            </div>
          </div>

          <div className="flex-shrink-0">{renderIconsSection()}</div>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer Menu */}
      <Drawer
        anchor="top"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        id={drawerId}
        aria-label={t('navigation.menu')}
        PaperProps={{
          style: {
            marginTop: '55px',
            maxHeight: 'calc(100vh - 64px)',
            overflowY: 'auto',
          },
        }}
      >
        <div
          className={`w-full ${isRtl ? 'rtl' : 'ltr'}`}
          style={{ backgroundColor: ACCENT_COLOR, color: 'white', minHeight: '100%' }}
        >
          <div
            className="flex justify-between items-center p-4 border-b"
            style={{ borderColor: ACCENT_COLOR_DARK }}
          >
            <Typography variant="h6" className="font-bold text-white">
              {t('common.appName')}
            </Typography>
            <IconButton
              color="inherit"
              onClick={handleMobileMenuClose}
              className="text-white"
              style={{ backgroundColor: 'transparent' }}
              aria-label={t('common.close')}
            >
              <CloseIcon />
            </IconButton>
          </div>
          <List style={{ backgroundColor: ACCENT_COLOR, color: 'white' }}>
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <ListItem
                  key={item.path}
                  component={Link}
                  to={item.path}
                  onClick={handleMobileMenuClose}
                  className="transition-colors nav-list-item"
                  style={{
                    color: 'white',
                    backgroundColor: 'transparent',
                    textDecoration: 'none',
                    textAlign: isRtl ? 'right' : 'left',
                  }}
                >
                  <ListItemText
                    primary={<span style={{ fontWeight: isActive ? 600 : 400 }}>{item.label}</span>}
                  />
                </ListItem>
              );
            })}

            {/* Mobile Resources Expandable Item */}
            <ListItem
              button
              onClick={() => setMobileResourcesOpen(!mobileResourcesOpen)}
              style={{ textAlign: isRtl ? 'right' : 'left', color: 'white' }}
            >
              <ListItemText primary={t('navigation.resources')} />
              {mobileResourcesOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItem>

            <Collapse in={mobileResourcesOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {/* Mobile National Insurance */}
                <ListItem
                  component={Link}
                  to="/national-insurance"
                  onClick={handleMobileMenuClose}
                  style={{
                    paddingLeft: isRtl ? 16 : 32,
                    paddingRight: isRtl ? 32 : 16,
                    textAlign: isRtl ? 'right' : 'left',
                    color: 'white',
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <ListItemText primary={t('navigation.nationalInsurance')} />
                </ListItem>

                {/* Mobile Service Areas */}
                <ListItem
                  button
                  onClick={() => setMobileServiceAreasOpen(!mobileServiceAreasOpen)}
                  style={{
                    paddingLeft: isRtl ? 16 : 32,
                    paddingRight: isRtl ? 32 : 16,
                    textAlign: isRtl ? 'right' : 'left',
                    color: 'white',
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <ListItemText primary={t('navigation.serviceAreas')} />
                  {mobileServiceAreasOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItem>
                <Collapse in={mobileServiceAreasOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {cities.map((city) => (
                      <ListItem
                        key={city.id}
                        component={Link}
                        to={`/cities/${city.id}`}
                        onClick={handleMobileMenuClose}
                        style={{
                          paddingLeft: isRtl ? 16 : 48,
                          paddingRight: isRtl ? 48 : 16,
                          textAlign: isRtl ? 'right' : 'left',
                          color: 'white',
                          backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        }}
                      >
                        <ListItemText primary={city.name[language]} />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>

                {/* Mobile Stone Types */}
                <ListItem
                  button
                  onClick={() => setMobileStoneTypesOpen(!mobileStoneTypesOpen)}
                  style={{
                    paddingLeft: isRtl ? 16 : 32,
                    paddingRight: isRtl ? 32 : 16,
                    textAlign: isRtl ? 'right' : 'left',
                    color: 'white',
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <ListItemText primary={t('navigation.stoneTypes')} />
                  {mobileStoneTypesOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItem>
                <Collapse in={mobileStoneTypesOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {stoneTypes.map((stone) => (
                      <ListItem
                        key={stone.id}
                        component={Link}
                        to={`/stones/${stone.slug}`}
                        onClick={handleMobileMenuClose}
                        style={{
                          paddingLeft: isRtl ? 16 : 48,
                          paddingRight: isRtl ? 48 : 16,
                          textAlign: isRtl ? 'right' : 'left',
                          color: 'white',
                          backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        }}
                      >
                        <ListItemText primary={stone.name[language]} />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </List>
            </Collapse>
          </List>
        </div>
      </Drawer>
    </React.Fragment>
  );
};

export default Navigation;
